import React, { useCallback, useEffect, useState } from 'react';
import { readContract, writeContract, waitForTransactionReceipt } from 'wagmi/actions';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { BigNumber, ethers } from 'ethers';
import { parseUnits } from 'viem';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import NavHeader from '../components/NavHeader.js';
import FooterSection from '../components/FooterSection.js';
import { GlassCard, ConnectWalletPrompt } from '../components/shared/GlassCard.js';
import { GradientText } from '../components/shared/GradientText.js';
import { config } from '../config.jsx';
import vaultAbi from '../config/vaultAbi.json';
import TokenAbi from '../config/TokenAbi.json';
import { coreVaultAddress, usdtAddress } from '../utils/addressHelpers.ts';
import { BSC_CHAIN_ID } from '../utils/constants.ts';
import { getStoredReferralCode } from '../components/RefLinkCapture.js';
import { referralTextToBytes32 } from '../utils/referralHelpers.js';


const MAX_APPROVAL =
  '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';

function formatUsdtAmount(raw) {
  if (raw == null) return '0';
  try {
    const bn = BigNumber.from(raw.toString());
    const n = parseFloat(ethers.utils.formatUnits(bn, 18));
    return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  } catch {
    return '0';
  }
}

/** Full-precision decimal string for Max / parseUnits (18 decimals). */
function rawToDecimalString(raw) {
  if (raw == null) return '';
  try {
    const bn = BigNumber.from(raw.toString());
    if (bn.isZero()) return '0';
    return ethers.utils.formatUnits(bn, 18);
  } catch {
    return '';
  }
}

function Portfolio() {
  const { address, chainId } = useAccount();
  const { open } = useAppKit();
  const [walletUsdt, setWalletUsdt] = useState(null);
  const [creditUserData, setCreditAmounts] = useState(null);
  const [rewardUserData, setRewardAmounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [amountInput, setAmountInput] = useState('');
  const [transferToInput, setTransferToInput] = useState('');
  /** Transfer modal: move vault credit vs reward balance (mutually exclusive). */
  const [transferAmountSource, setTransferAmountSource] = useState('credit');
  /** Reward tab: optional custom recipient (default is connected wallet). */
  const [transferRewardCustomRecipient, setTransferRewardCustomRecipient] = useState(false);
  const [txPending, setTxPending] = useState(false);
  /** Factory `getUserRewardData` (same call as Portfolio). */
  const [poolRewardData, setPoolRewardData] = useState(null);
  const loadBalances = useCallback(async () => {
    if (!address || chainId !== BSC_CHAIN_ID) {
      setWalletUsdt(null);
      setCreditAmounts(null);
      setRewardAmounts(null);
      setPoolRewardData(null);
      return;
    }
    setLoading(true);
    try {
      const cookieRef = getStoredReferralCode();
      const refStr =
        cookieRef && typeof cookieRef === 'string' && cookieRef.length >= 4 ? cookieRef : '';
      const refBytes32 = referralTextToBytes32(refStr);
      const [userDatas] = await Promise.all([
        readContract(config, {
          address: coreVaultAddress,
          abi: vaultAbi,
          functionName: 'getUserDatas',
          args: [address, refBytes32],
          chainId: BSC_CHAIN_ID,
        }),
      ]);
      setWalletUsdt(userDatas[0][0]);
      setCreditAmounts(userDatas[0][1]);
      setRewardAmounts(userDatas[0][4]);

      const currentMonthIndex = new Date().getFullYear() * 100 + new Date().getMonth() + 1;
      try {
        const [rewardArr] = await Promise.all([
          readContract(config, {
            address: coreVaultAddress,
            abi: vaultAbi,
            functionName: 'getUserRewardData',
            args: [address, currentMonthIndex],
            chainId: BSC_CHAIN_ID,
          }),
        ]);
        setPoolRewardData(Array.isArray(rewardArr) ? [...rewardArr] : null);
      } catch (rewardErr) {
        console.error(rewardErr);
        setPoolRewardData(null);
      }
    } catch (e) {
      console.error(e);
      toast.error('Could not load credit data');
      setWalletUsdt(null);
      setCreditAmounts(null);
      setRewardAmounts(null);
      setPoolRewardData(null);
    } finally {
      setLoading(false);
    }
  }, [address, chainId]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const withdrawableRaw = rewardUserData;
  const vaultCreditRaw = creditUserData;

  const setMaxDeposit = () => {
    setAmountInput(rawToDecimalString(walletUsdt));
  };

  const setMaxWithdraw = () => {
    setAmountInput(rawToDecimalString(withdrawableRaw));
  };

  const setMaxTransfer = () => {
    const raw = transferAmountSource === 'credit' ? vaultCreditRaw : rewardUserData;
    setAmountInput(rawToDecimalString(raw));
  };

  const openModal = (type) => {
    setModal(type);
    setAmountInput('');
    setTransferToInput('');
    if (type === 'transfer') {
      setTransferAmountSource('credit');
      setTransferRewardCustomRecipient(false);
    }
  };

  const closeModal = () => {
    if (txPending) return;
    setModal(null);
    setAmountInput('');
    setTransferToInput('');
    setTransferAmountSource('credit');
    setTransferRewardCustomRecipient(false);
  };

  const runDeposit = async () => {
    if (!address || chainId !== BSC_CHAIN_ID) {
      toast.error('Connect on BNB Smart Chain');
      return;
    }
    let amountWei;
    try {
      amountWei = parseUnits(amountInput.trim() || '0', 18);
    } catch {
      toast.error('Invalid amount');
      return;
    }
    const amountBn = BigNumber.from(amountWei.toString());
    if (amountBn.isZero()) {
      toast.error('Enter an amount greater than zero');
      return;
    }
    const walletBn =
      walletUsdt != null ? BigNumber.from(walletUsdt.toString()) : BigNumber.from(0);
    if (amountBn.gt(walletBn)) {
      toast.error('Amount exceeds wallet USDT balance');
      return;
    }
    try {
      setTxPending(true);
      const allowance = await readContract(config, {
        address: usdtAddress,
        abi: TokenAbi,
        functionName: 'allowance',
        args: [address, coreVaultAddress],
        chainId: BSC_CHAIN_ID,
      });
      const allowanceBn = BigNumber.from(allowance.toString());
      if (allowanceBn.lt(amountBn)) {
        const approveHash = await writeContract(config, {
          address: usdtAddress,
          abi: TokenAbi,
          functionName: 'approve',
          args: [coreVaultAddress, MAX_APPROVAL],
          chainId: BSC_CHAIN_ID,
        });
        await waitForTransactionReceipt(config, { hash: approveHash });
      }
      const depositHash = await writeContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'deposit',
        args: [amountWei],
        chainId: BSC_CHAIN_ID,
      });
      await waitForTransactionReceipt(config, { hash: depositHash });
      toast.success('Deposit confirmed');
      closeModal();
      await loadBalances();
    } catch (e) {
      console.error(e);
      toast.error(e?.shortMessage || e?.message || 'Deposit failed');
    } finally {
      setTxPending(false);
    }
  };

  const runWithdraw = async () => {
    if (!address || chainId !== BSC_CHAIN_ID) {
      toast.error('Connect on BNB Smart Chain');
      return;
    }
    let amountWei;
    try {
      amountWei = parseUnits(amountInput.trim() || '0', 18);
    } catch {
      toast.error('Invalid amount');
      return;
    }
    const amountBn = BigNumber.from(amountWei.toString());
    if (amountBn.isZero()) {
      toast.error('Enter an amount greater than zero');
      return;
    }
    const maxOutBn =
      withdrawableRaw != null
        ? BigNumber.from(withdrawableRaw.toString())
        : BigNumber.from(0);
    if (amountBn.gt(maxOutBn)) {
      toast.error('Amount exceeds withdrawable balance');
      return;
    }
    try {
      setTxPending(true);
      const hash = await writeContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'withdrawReward',
        args: [amountWei],
        chainId: BSC_CHAIN_ID,
      });
      await waitForTransactionReceipt(config, { hash });
      toast.success('Withdrawal confirmed');
      closeModal();
      await loadBalances();
    } catch (e) {
      console.error(e);
      toast.error(e?.shortMessage || e?.message || 'Withdraw failed');
    } finally {
      setTxPending(false);
    }
  };

  const runTransfer = async () => {
    if (!address || chainId !== BSC_CHAIN_ID) {
      toast.error('Connect on BNB Smart Chain');
      return;
    }
    let recipient;
    if (transferAmountSource === 'credit') {
      try {
        recipient = ethers.utils.getAddress(transferToInput.trim());
      } catch {
        toast.error('Invalid recipient address');
        return;
      }
      if (recipient.toLowerCase() === address.toLowerCase()) {
        toast.error('Use another wallet as the recipient');
        return;
      }
    } else if (transferRewardCustomRecipient) {
      try {
        recipient = ethers.utils.getAddress(transferToInput.trim());
      } catch {
        toast.error('Invalid recipient address');
        return;
      }
      if (recipient.toLowerCase() === address.toLowerCase()) {
        toast.error('Use another wallet as the recipient');
        return;
      }
    } else {
      recipient = ethers.utils.getAddress(address);
    }
    let amountWei;
    try {
      amountWei = parseUnits(amountInput.trim() || '0', 18);
    } catch {
      toast.error('Invalid amount');
      return;
    }
    const amountBn = BigNumber.from(amountWei.toString());
    if (amountBn.isZero()) {
      toast.error('Enter an amount greater than zero');
      return;
    }
    const maxRaw = transferAmountSource === 'credit' ? vaultCreditRaw : rewardUserData;
    const maxBn =
      maxRaw != null ? BigNumber.from(maxRaw.toString()) : BigNumber.from(0);
    if (amountBn.gt(maxBn)) {
      toast.error(
        transferAmountSource === 'credit'
          ? 'Amount exceeds your credit balance'
          : 'Amount exceeds your reward balance',
      );
      return;
    }
    try {
      setTxPending(true);
      const fn = transferAmountSource === 'credit' ? 'transferCredit' : 'convertCredit';
      const hash = await writeContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: fn,
        args: [recipient, amountWei],
        chainId: BSC_CHAIN_ID,
      });
      await waitForTransactionReceipt(config, { hash });
      toast.success('Transfer confirmed');
      closeModal();
      await loadBalances();
    } catch (e) {
      console.error(e);
      toast.error(e?.shortMessage || e?.message || 'Transfer failed');
    } finally {
      setTxPending(false);
    }
  };

  // /** Pool rewards slot — same as `RewardsSection` (`poolDatas[4]`). */
  // const poolRewardClaimableWei = (() => {
  //   if (!poolRewardData || poolRewardData[4] == null) return BigNumber.from(0);
  //   try {
  //     return BigNumber.from(poolRewardData[4].toString());
  //   } catch {
  //     return BigNumber.from(0);
  //   }
  // })();


  // const claimPoolRewardDisabled =
  //   loading ||
  //   txPending ||
  //   poolRewardClaimableWei.isZero() ||
  //   poolRewardClaimableWei.lt(FACTORY_POOL_REWARD_MIN_WEI);

  // const handleClaimFactoryPoolReward = async () => {
  //   if (!address || chainId !== BSC_CHAIN_ID) {
  //     toast.error('Connect on BNB Smart Chain');
  //     return;
  //   }

  //   if (claimPoolRewardDisabled) return;
  //   try {
  //     setTxPending(true);
  //     const hash = await writeContract(config, {
  //       address: coreVaultAddress,
  //       abi: vaultAbi,
  //       functionName: 'claimReward',
  //       chainId: BSC_CHAIN_ID,
  //     });
  //     await waitForTransactionReceipt(config, { hash });
  //     toast.success('USDT claimed successfully');
  //     await loadBalances();
  //   } catch (err) {
  //     console.error(err);
  //     toast.error(err?.shortMessage || err?.message || 'Claim failed. Please try again.');
  //   } finally {
  //     setTxPending(false);
  //   }
  // };

  if (!address) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark">
        <NavHeader />
        <main className="pt-nav pb-24 px-4 flex justify-center">
          <ConnectWalletPrompt message="Connect your wallet to view credit and deposit USDT" />
        </main>
        <FooterSection />
      </div>
    );
  }

  if (chainId !== BSC_CHAIN_ID) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark py-32">
        <NavHeader />
        <main className="pt-nav pb-36 px-4 flex justify-center">
          <GlassCard variant="gold" className="p-8 max-w-md text-center">
            <p className="text-gray-300 mb-4">Credit is available on BNB Smart Chain (BSC).</p>
            <button type="button" className="btn-gold px-6 py-2 text-sm" onClick={() => open()}>
              Switch network in wallet
            </button>
          </GlassCard>
        </main>
        <FooterSection />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark relative">
      {loading && (
        <>
          <style>
            {`
              @keyframes hmPortfolioBarSlide {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(400%); }
              }
              .hm-portfolio-bar-fill {
                width: 40%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(238, 212, 91, 0.95), transparent);
                animation: hmPortfolioBarSlide 1.1s ease-in-out infinite;
              }
            `}
          </style>
          <div
            className="fixed left-0 right-0 top-20 z-[60] flex flex-col border-b border-hash-gold/10 shadow-[0_4px_24px_rgba(0,0,0,0.35)]"
            aria-busy="true"
          >
            <div
              className="h-1 w-full overflow-hidden bg-hash-gold/15 shadow-[0_1px_0_rgba(0,0,0,0.4)]"
              role="progressbar"
              aria-valuetext="Loading portfolio"
            >
              <div className="hm-portfolio-bar-fill" />
            </div>
            <div
              className="flex items-center justify-center gap-2 border-b border-hash-gold/15 bg-dark/95 py-2 px-3 backdrop-blur-md sm:py-2.5"
              role="status"
              aria-live="polite"
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-hash-gold shadow-[0_0_8px_rgba(238,212,91,0.6)] motion-safe:animate-pulse"
                aria-hidden
              />
              <span className="text-center text-[11px] font-medium tracking-wide text-hash-gold/95 sm:text-xs">
                Loading vault balances and pool reward data…
              </span>
            </div>
          </div>
        </>
      )}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: 'url(/images/background-games.jpg)',
          opacity: 0.12,
        }}
      />
      <NavHeader />

      <main
        className={`pb-16 px-4 sm:px-6 lg:px-8 relative z-10 ${loading ? 'pt-nav-loading' : 'pt-nav'}`}
      >
        <div className="container-premium max-w-4xl mx-auto">
          <h1 className="page-title text-3xl sm:text-4xl font-bold text-center">
            <GradientText variant="hash" className="text-3xl sm:text-4xl font-bold">
              Credit
            </GradientText>
          </h1>
          <p className="page-subtitle text-gray-400 text-center">
            USDT in your wallet and credit held in the core vault
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <GlassCard variant="gold" className="p-6 text-center">
              <div className="text-gray-500 text-sm mb-2">USDT balance</div>
              <div className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold">
                {loading ? '…' : `${formatUsdtAmount(walletUsdt)} USDT`}
              </div>
              <div className="text-gray-600 text-xs mt-1">In your wallet</div>
            </GlassCard>
            <GlassCard variant="gold" className="p-6 text-center">
              <div className="text-gray-500 text-sm mb-2">Credit amounts</div>
              <div className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold">
                {loading ? '…' : `${formatUsdtAmount(vaultCreditRaw)} Credits`}
              </div>
              <div className="text-gray-600 text-xs mt-1">Vault record</div>
            </GlassCard>
            <GlassCard variant="gold" className="p-6 text-center sm:col-span-2 lg:col-span-1">
              <div className="text-gray-500 text-sm mb-2">Reward amounts</div>
              <div className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold">
                {loading ? '…' : `${formatUsdtAmount(rewardUserData)} USDT`}
              </div>
              <div className="text-gray-600 text-xs mt-1">Vault record</div>
            </GlassCard>
            <GlassCard variant="gold" className="p-6 text-center sm:col-span-2 lg:col-span-1">
              <div className="text-gray-500 text-sm mb-2">Pool Points</div>
              <div className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold">
                {loading ? '…' : `${Number(poolRewardData?.[6] ?? 0).toLocaleString()} Point`}
              </div>
              <div className="text-gray-600 text-xs mt-1">Vault record</div>
            </GlassCard>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center py-10">
            <button
              type="button"
              className="btn-gold px-8 py-3 text-sm font-semibold rounded-lg"
              onClick={() => openModal('deposit')}
              disabled={loading}
            >
              Deposit
            </button>
            <button
              type="button"
              className="px-8 py-3 text-sm font-semibold rounded-lg border border-hash-gold/40 text-hash-gold hover:bg-hash-gold/10 transition-colors cursor-pointer"
              onClick={() => openModal('transfer')}
              disabled={loading}
            >
              Transfer
            </button>
            <button
              type="button"
              className="px-8 py-3 text-sm font-semibold rounded-lg border border-hash-gold/40 text-hash-gold hover:bg-hash-gold/10 transition-colors cursor-pointer"
              onClick={() => openModal('withdraw')}
              disabled={loading}
            >
              Withdraw
            </button>
            <Link
              to="/affiliates"
              className="px-8 py-3 text-sm font-semibold rounded-lg border border-hash-gold/40 text-hash-gold hover:bg-hash-gold/10 transition-colors text-center"
            >
              Affiliates
            </Link>
          </div>

        </div>
      </main>

      {modal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <GlassCard variant="premium" className="p-6 w-full max-w-md relative z-[101]">
            <>
              <h2 className="text-lg font-bold text-white mb-4">
                  {modal === 'deposit'
                    ? 'Deposit USDT'
                    : modal === 'withdraw'
                      ? 'Withdraw Reward'
                      : 'Transfer credits'}
                </h2>
                {modal === 'transfer' && (
                  <>
                    <div className="mb-4 rounded-xl border border-hash-gold/15 bg-dark-surface/40 p-3">
                      <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">
                        Transfer from
                      </div>
                      <div
                        className="flex rounded-lg border border-hash-gold/20 bg-dark-secondary/80 p-0.5"
                        role="tablist"
                        aria-label="Transfer balance type"
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={transferAmountSource === 'credit'}
                          className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors cursor-pointer ${transferAmountSource === 'credit'
                            ? 'bg-hash-gold/20 text-hash-gold shadow-sm'
                            : 'text-gray-400 hover:text-white'
                            }`}
                          onClick={() => {
                            setTransferAmountSource('credit');
                            setAmountInput('');
                            setTransferRewardCustomRecipient(false);
                          }}
                          disabled={txPending}
                        >
                          Credit amount
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={transferAmountSource === 'reward'}
                          className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors cursor-pointer ${transferAmountSource === 'reward'
                            ? 'bg-hash-gold/20 text-hash-gold shadow-sm'
                            : 'text-gray-400 hover:text-white'
                            }`}
                          onClick={() => {
                            setTransferAmountSource('reward');
                            setAmountInput('');
                            setTransferToInput('');
                            setTransferRewardCustomRecipient(false);
                          }}
                          disabled={txPending}
                        >
                          Reward amount
                        </button>
                      </div>
                      <p className="mt-2 text-center text-xs text-gray-500">
                        Available:{' '}
                        <span className="font-mono font-semibold text-hash-gold tabular-nums">
                          {loading
                            ? '…'
                            : transferAmountSource === 'credit'
                              ? `${formatUsdtAmount(vaultCreditRaw)} Credits`
                              : `${formatUsdtAmount(rewardUserData)} USDT`}
                        </span>
                      </p>
                    </div>
                    {transferAmountSource === 'credit' && (
                      <>
                        <label className="text-gray-400 text-sm mb-2 mt-4 block" htmlFor="credit-transfer-to">
                          Recipient wallet address
                        </label>
                        <input
                          id="credit-transfer-to"
                          type="text"
                          spellCheck={false}
                          autoComplete="off"
                          className="w-full rounded-lg bg-dark-secondary border border-hash-gold/20 px-4 py-3 text-white mb-1 focus:outline-none focus:border-hash-gold/50 font-mono text-sm"
                          placeholder="0x…"
                          value={transferToInput}
                          onChange={(e) => setTransferToInput(e.target.value)}
                          disabled={txPending}
                        />
                        <p className="text-gray-500 text-xs mb-4">Credits are sent to this address.</p>
                      </>
                    )}
                    {transferAmountSource === 'reward' && (
                      <div className="mt-4 space-y-3">
                        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-hash-gold/15 bg-dark-secondary/50 px-3 py-3 text-left">
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-hash-gold/40 bg-dark-secondary text-hash-gold focus:ring-hash-gold/40"
                            checked={transferRewardCustomRecipient}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setTransferRewardCustomRecipient(on);
                              if (!on) setTransferToInput('');
                            }}
                            disabled={txPending}
                          />
                          <span>
                            <span className="block text-sm font-semibold text-white">
                              Send reward to another wallet
                            </span>
                            <span className="mt-0.5 block text-xs text-gray-500">
                              Off: your connected wallet. On: enter another address (same rules as credit).
                            </span>
                          </span>
                        </label>
                        {transferRewardCustomRecipient && (
                          <>
                            <label
                              className="text-gray-400 text-sm mb-2 block"
                              htmlFor="reward-transfer-to"
                            >
                              Recipient wallet address
                            </label>
                            <input
                              id="reward-transfer-to"
                              type="text"
                              spellCheck={false}
                              autoComplete="off"
                              className="w-full rounded-lg bg-dark-secondary border border-hash-gold/20 px-4 py-3 text-white mb-1 focus:outline-none focus:border-hash-gold/50 font-mono text-sm"
                              placeholder="0x…"
                              value={transferToInput}
                              onChange={(e) => setTransferToInput(e.target.value)}
                              disabled={txPending}
                            />
                            <p className="text-gray-500 text-xs mb-1">Rewards are sent to this address.</p>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label
                    className="text-gray-400 text-sm"
                    htmlFor={modal === 'transfer' ? 'credit-transfer-amount' : 'credit-amount'}
                  >
                    {modal === 'transfer'
                      ? transferAmountSource === 'credit'
                        ? 'Credit amount'
                        : 'Reward amount'
                      : 'Amount (USDT)'}
                  </label>
                  <button
                    type="button"
                    className="text-sm font-semibold text-hash-gold hover:text-white disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
                    onClick={
                      modal === 'deposit'
                        ? setMaxDeposit
                        : modal === 'withdraw'
                          ? setMaxWithdraw
                          : setMaxTransfer
                    }
                    disabled={
                      txPending ||
                      loading ||
                      (modal === 'transfer' &&
                        (transferAmountSource === 'credit' ? vaultCreditRaw == null : rewardUserData == null))
                    }
                  >
                    Max
                  </button>
                </div>
                <input
                  id={modal === 'transfer' ? 'credit-transfer-amount' : 'credit-amount'}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="w-full rounded-lg bg-dark-secondary border border-hash-gold/20 px-4 py-3 text-white mb-6 focus:outline-none focus:border-hash-gold/50"
                  placeholder="0.0"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  disabled={txPending}
                />
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer"
                    onClick={closeModal}
                    disabled={txPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-gold px-5 py-2 text-sm"
                    onClick={
                      modal === 'deposit'
                        ? runDeposit
                        : modal === 'withdraw'
                          ? runWithdraw
                          : runTransfer
                    }
                    disabled={txPending}
                  >
                    {txPending ? 'Confirm in wallet…' : 'Confirm'}
                  </button>
                </div>
            </>
          
          </GlassCard>
        </div>
      )}

      <FooterSection />
    </div>
  );
}

export default Portfolio;
